// src/components/FieldInfoPanel.jsx
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

export default function FieldInfoPanel({
  field,
  group,
  collapsed,
  onCollapse,
  onExpand,
  onClose,
  onOpenDetail,
  onOpenTasks,
}) {
  const navigate = useNavigate();

  const title = useMemo(() => {
    if (!field) return "Поле не вибрано";
    return field?.name || `Поле #${field?.id ?? "—"}`;
  }, [field]);

  const area = useMemo(() => {
    if (!field) return "—";
    const v = Number(field?.areaHa || 0);
    return isFinite(v) ? `${v.toFixed(2)} ha` : "—";
  }, [field]);

  const groupName = useMemo(() => {
    if (!field) return "—";
    return group?.name || field?.groupName || field?.group?.name || "—";
  }, [field, group]);

  const note = useMemo(() => {
    const n = String(field?.note || field?.notes || "").trim();
    return n || null;
  }, [field]);

  const handleOpenTasks = () => {
    if (!field?.id) return;

    // Якщо батько вже передає колбек — викличемо його (як у тебе логіка в Main.jsx)
    if (typeof onOpenTasks === "function") {
      onOpenTasks(field.id);
      return;
    }

    // Фолбек: напряму навігація на /tasks?fieldId=...
    navigate(`/tasks?fieldId=${encodeURIComponent(field.id)}`);
  };

  const handleOpenDetail = () => {
    if (!field?.id) return;

    if (typeof onOpenDetail === "function") {
      onOpenDetail(field.id);
      return;
    }

    // якщо раптом немає колбека
    navigate(`/fields/${encodeURIComponent(field.id)}`);
  };

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={onExpand}
        title="Розгорнути інфо"
        style={{
          position: "absolute",
          left: 12,
          bottom: 12,
          zIndex: 22,
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 12px",
          borderRadius: 999,
          border: "1px solid rgba(255,255,255,0.14)",
          background: "rgba(0,0,0,0.45)",
          backdropFilter: "blur(14px)",
          boxShadow: "0 18px 50px rgba(0,0,0,0.30)",
          color: "rgba(255,255,255,0.92)",
          fontWeight: 950,
          cursor: "pointer",
          maxWidth: "calc(100% - 24px)",
        }}
      >
        <span style={{ fontWeight: 950 }}>ℹ️</span>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</span>
        <span style={{ opacity: 0.8 }}>👁</span>
      </button>
    );
  }

  return (
    <div
      style={{
        position: "absolute",
        left: 12,
        bottom: 12,
        zIndex: 22,
        width: "clamp(320px, 42vw, 420px)",
        maxWidth: "calc(100% - 24px)",
        borderRadius: 18,
        border: "1px solid rgba(255,255,255,0.14)",
        background:
          "radial-gradient(800px 260px at 0% 0%, rgba(34,197,94,0.18), transparent 55%), rgba(0,0,0,0.45)",
        backdropFilter: "blur(14px)",
        boxShadow: "0 18px 50px rgba(0,0,0,0.35)",
        padding: 12,
        color: "rgba(255,255,255,0.92)",
        boxSizing: "border-box",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <div style={{ fontWeight: 950, letterSpacing: 0.2, fontSize: 13, opacity: 0.9 }}>Інформація по полю</div>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button type="button" onClick={onCollapse} title="Згорнути" style={iconBtn()}>
            🙈
          </button>
          <button
            type="button"
            onClick={onClose}
            title="Закрити"
            style={iconBtn("rgba(251,113,133,0.20)", "rgba(251,113,133,0.35)")}
          >
            ✕
          </button>
        </div>
      </div>

      <div
        title={title}
        style={{
          marginTop: 6,
          fontWeight: 950,
          fontSize: 15,
          lineHeight: 1.15,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {title}
      </div>

      <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
        <InfoPill label="📏 Площа" value={area} />
        <InfoPill label="📦 Група" value={groupName} />
      </div>

      {note ? (
        <div
          style={{
            marginTop: 10,
            padding: "10px 10px",
            borderRadius: 14,
            border: "1px solid rgba(255,255,255,0.10)",
            background: "rgba(0,0,0,0.18)",
            color: "rgba(255,255,255,0.88)",
            fontWeight: 850,
            lineHeight: 1.35,
            maxHeight: 96,
            overflow: "auto",
          }}
        >
          {note}
        </div>
      ) : (
        <div style={{ marginTop: 10, opacity: 0.72, fontWeight: 900, fontSize: 12 }}>
          Нотаток немає — можна додати на сторінці поля.
        </div>
      )}

      <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button type="button" className="btn" onClick={handleOpenDetail} disabled={!field}>
          Відкрити поле →
        </button>

        {/* ✅ тепер гарантовано відкриває Tasks з fieldId */}
        <button type="button" className="btn primary" onClick={handleOpenTasks} disabled={!field}>
          Задачі по полю →
        </button>
      </div>

      <style>{`
        .btn{
          border: 1px solid rgba(255,255,255,0.14);
          background: rgba(255,255,255,0.06);
          color: rgba(255,255,255,0.92);
          border-radius: 14px;
          padding: 10px 12px;
          font-weight: 950;
          cursor: pointer;
        }
        .btn:disabled{
          opacity: .55;
          cursor: not-allowed;
        }
        .btn.primary{
          border-color: rgba(34,197,94,0.35);
          background: rgba(34,197,94,0.16);
        }
        @media (max-width: 640px){
          .btn{ padding: 10px 12px !important; flex: 1 1 auto; }
        }
      `}</style>
    </div>
  );
}

function InfoPill({ label, value }) {
  return (
    <div
      style={{
        padding: "8px 10px",
        borderRadius: 14,
        border: "1px solid rgba(255,255,255,0.12)",
        background: "rgba(255,255,255,0.04)",
        fontWeight: 900,
        fontSize: 12,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        minWidth: 0,
      }}
    >
      <span style={{ opacity: 0.86, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
      <span style={{ fontWeight: 950, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {value}
      </span>
    </div>
  );
}

function iconBtn(bg = "rgba(255,255,255,0.04)", border = "rgba(255,255,255,0.14)") {
  return {
    flex: "0 0 auto",
    width: 40,
    height: 40,
    borderRadius: 14,
    display: "grid",
    placeItems: "center",
    fontWeight: 950,
    border: `1px solid ${border}`,
    background: bg,
    color: "rgba(255,255,255,0.92)",
    cursor: "pointer",
    lineHeight: 1,
    padding: 0,
  };
}